import React, { useEffect, useState, useRef } from 'react';
import { flowApi, actionApi, deviceApi } from '../../services/eventSystemApiService';
import { useEventSystemCache } from '../../context/EventSystemCacheContext';
import { getAuth } from 'firebase/auth';
import { useTenant } from '@libs/contexts/TenantContext';
import { actionCacheKey } from '../../services/eventSystem/actionCacheScope';

// Module-scoped cooldown map to avoid repeated preloading across remounts
const LAST_PRELOAD_BY_TENANT = new Map();
const PRELOAD_COOLDOWN_MS = 30_000; // 30 seconds

/**
 * EventSystemDataPreloader - Invisible component that preloads all Event System data
 * This component should be mounted on the parent layout of all Event System pages
 * to ensure data is fetched as early as possible and cached for immediate access.
 */
const EventSystemDataPreloader = () => {
  // If the global provider is active, skip preloading to avoid duplicate calls
  if (typeof window !== 'undefined' && window.__ES_PROVIDER_ACTIVE) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('EventSystemDataPreloader: Skipping because provider is active');
    }
    return null;
  }

  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getCache, updateCache } = useEventSystemCache();
  const { selectedTenant } = useTenant();
  // Guards to prevent repeated fetches
  const inFlightRef = useRef(false);
  const lastPreloadedTenantRef = useRef(null);

  // Get current user once on mount
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        // Get user claims to get tenantId
        user.getIdTokenResult().then(idTokenResult => {
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            tenantId: idTokenResult.claims.tenantId || null,
          });
        });
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Preload all Event System data once we have user context and selected tenant
  useEffect(() => {
    if (!currentUser || !selectedTenant) return;
    const tenantId = selectedTenant?.id;
    if (!tenantId) return;

    // Skip if we've already successfully preloaded for this tenant in this session
    if (lastPreloadedTenantRef.current === tenantId) {
      return;
    }

    // Cooldown guard (module-scoped + sessionStorage fallback)
    const now = Date.now();
    const lastPreloadTs = LAST_PRELOAD_BY_TENANT.get(tenantId) ?? Number(sessionStorage.getItem(`es_last_preload_${tenantId}`) || 0);
    if (now - lastPreloadTs < PRELOAD_COOLDOWN_MS) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`EventSystemDataPreloader: Skipping due to cooldown for tenant ${tenantId}`);
      }
      return;
    }

    const preloadAllData = async () => {
      if (inFlightRef.current) return; // prevent overlapping calls

      // Check if we need to fetch any data
      const cachedFlows = getCache(actionCacheKey('flows', tenantId));
      const cachedActions = getCache(actionCacheKey('actions', tenantId));
      const cachedDevices = getCache(actionCacheKey('devices', tenantId));

      // If all data is cached and fresh, no need to fetch
      if (cachedFlows && cachedActions && cachedDevices) {
        // Mark as preloaded for this tenant to avoid repeating
        lastPreloadedTenantRef.current = tenantId;
        if (process.env.NODE_ENV !== 'production') {
          console.log('EventSystemDataPreloader: All data already cached');
        }
        return;
      }

      inFlightRef.current = true;
      setIsLoading(true);
      if (process.env.NODE_ENV !== 'production') {
        console.log('EventSystemDataPreloader: Fetching all data for tenant:', tenantId);
      }
      
      try {
        // Stamp cooldown immediately to prevent stampedes
        LAST_PRELOAD_BY_TENANT.set(tenantId, now);
        sessionStorage.setItem(`es_last_preload_${tenantId}`, String(now));
        if (process.env.NODE_ENV !== 'production') {
          console.log(`EventSystemDataPreloader: Using tenant ID: ${tenantId} for API calls`);
        }
        
        // Fetch all data in parallel
        const [flowsResponse, actionsResponse, devicesResponse] = await Promise.all([
          flowApi.getAll(tenantId),
          actionApi.getAll(tenantId),
          deviceApi.getAll(tenantId)
        ]);
        
        // Ensure each response is an array before caching
        const flows = Array.isArray(flowsResponse) ? flowsResponse : [];
        const actions = Array.isArray(actionsResponse) ? actionsResponse : [];
        const devices = Array.isArray(devicesResponse) ? devicesResponse : [];
        
        // Cache any newly fetched data
        if (!cachedFlows) updateCache(actionCacheKey('flows', tenantId), flows);
        if (!cachedActions) updateCache(actionCacheKey('actions', tenantId), actions);
        if (!cachedDevices) updateCache(actionCacheKey('devices', tenantId), devices);
        // Mark tenant as preloaded
        lastPreloadedTenantRef.current = tenantId;
        LAST_PRELOAD_BY_TENANT.set(tenantId, Date.now());
        sessionStorage.setItem(`es_last_preload_${tenantId}`, String(Date.now()));

        if (process.env.NODE_ENV !== 'production') {
          console.log('EventSystemDataPreloader: All data preloaded successfully');
        }
      } catch (error) {
        console.error('EventSystemDataPreloader: Error preloading data:', error);
        // Even on error, we don't show any UI - the individual components will handle errors
      } finally {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    };
    
    preloadAllData();
    // Intentionally do not include getCache/updateCache in deps to avoid unstable identity loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, selectedTenant?.id]);
  
  // This component doesn't render anything - it's just for data preloading
  return null;
};

export default EventSystemDataPreloader;
